import { rem } from 'polished'
import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import SolidPlusCircleIcon from 'LuxKit/Icons/solid/SolidPlusCircleIcon'
import SolidAngleDownIcon from 'LuxKit/Icons/solid/SolidAngleDownIcon'
import Group from 'components/utils/Group'
import TextLink from 'LuxKit/TextLink'
import type { FullTrip } from 'tripPlanner/types/common'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import NLPItemCreate from '../NLPItemCreate/NLPItemCreate'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import InlineItemCreateContext from 'tripPlanner/contexts/InlineItemCreate/InlineItemCreateContext'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import NLPItemAdd from '../TripModal/TripItems/NLPItemAdd'
import { useScreenSizeOnly } from 'hooks/useScreenSize'

const Root = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(16)} ${rem(20)};

  @container (min-width: 640px) {
    padding: ${rem(12)} ${rem(40)};
  }

  @media print {
    display: none;
  }
`

const CloseContainer = styled.div`
  flex: 1;
  text-align: right;
`

const Balancer = styled.div`
  flex: 1;
  display: none;
  @container (min-width: 640px) {
    display: block;
  }
`

interface Props {
  trip: FullTrip
  date: string
}

function TimelineNLPAddItem(props: Props) {
  const { trip, date } = props
  const intl = useIntl()

  const openModal = useOpenModal()

  const { selectedDate, setDate, clearDate: closeUi } = useContext(InlineItemCreateContext)
  const uiOpen = selectedDate === date

  const isMobile = useScreenSizeOnly('mobile')

  const openUi = useCallback(() => {
    if (isMobile) {
      openModal(NLPItemAdd, { tripId: trip.id, defaultDate: date })
    } else {
      setDate(date)
    }
  }, [date, isMobile, openModal, setDate, trip.id])

  return (
    <Root>
      {!uiOpen && <Group direction="horizontal" horizontalAlign="center">
        <TextLink onClick={openUi} startIcon={<SolidPlusCircleIcon />} weight="regular" size="medium">
          {intl.formatMessage({
            id: '5xBsch6VZO3krn_L3x3z',
            defaultMessage: 'Add something to this day',
            description: 'Link to add an activity or item to a specific day in the trip itinerary',
          })}
        </TextLink>
      </Group>}

      {uiOpen && <VerticalSpacer gap={12}>
        <Group direction="vertical" tabletDirection="horizontal-reverse" horizontalAlign="stretch" tabletHorizontalAlign="center" gap={4}>
          <CloseContainer><TextLink onClick={closeUi} endIcon={<LineTimesIcon />} underline={false} weight="regular" size="medium">
            {intl.formatMessage({
              id: 'rDubqcelAhvNR8LYd1RK',
              defaultMessage: 'Close',
              description: 'Button to close the inline item creation input on the form',
            })}
          </TextLink>
          </CloseContainer>
          <BodyTextBlock variant="medium" startIcon={<SolidAngleDownIcon />} align="center">
            {intl.formatMessage({
              id: 'cnQwjfwstw3uPo4YUAfx',
              defaultMessage: 'What would you like to do? Type your plan',
              description: 'Prompt text encouraging user to type what activity they want to add to their day',
            })}
          </BodyTextBlock>
          <Balancer></Balancer>
        </Group>
        <NLPItemCreate trip={trip} date={date} onItemCreated={closeUi} />
      </VerticalSpacer>}
    </Root>
  )
}

export default TimelineNLPAddItem
